// UPost.com - KDP Account Setup Guide
// Complete guide on business/tax requirements for Amazon KDP
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { 
  CheckCircle, ChevronDown, ChevronRight, AlertTriangle, 
  FileText, DollarSign, Building, Globe, Shield, BookOpen,
  ExternalLink, Copy, Check
} from 'lucide-react';

export function KDPSetupGuide() {
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    requirements: true,
    taxInfo: false,
    bankInfo: false,
    businessStructure: false,
    international: false,
    commonMistakes: false
  });
  const [copiedItem, setCopiedItem] = useState(null);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({...prev, [section]: !prev[section]}));
  };

  const copyToClipboard = (text, item) => {
    navigator.clipboard.writeText(text);
    setCopiedItem(item);
    setTimeout(() => setCopiedItem(null), 2000);
  };

  const Section = ({ id, title, icon: Icon, children }) => (
    <Card className="mb-4">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => toggleSection(id)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-xl">
            <Icon className="w-6 h-6 text-purple-600" />
            {title}
          </CardTitle>
          {expandedSections[id] ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </CardHeader>
      {expandedSections[id] && (
        <CardContent className="pt-0">
          {children}
        </CardContent>
      )}
    </Card>
  );

  const CheckItem = ({ children }) => (
    <div className="flex items-start gap-3 py-2">
      <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );

  const WarningItem = ({ children }) => (
    <div className="flex items-start gap-3 py-2 bg-yellow-50 px-3 rounded-lg">
      <AlertTriangle className="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" />
      <div className="text-yellow-800">{children}</div>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-yellow-50">
        <div className="container mx-auto px-4 py-12">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <BookOpen className="w-4 h-4" />
              Complete Setup Guide
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Amazon KDP Account Setup
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to know about business and tax requirements to open your 
              Amazon Kindle Direct Publishing account - information they never tell you upfront!
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Quick Start Checklist */}
            <Card className="mb-8 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-blue-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  ✅ Quick Start Checklist
                </CardTitle>
                <CardDescription>
                  Have these ready before you start
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <CheckItem>Valid email address</CheckItem>
                    <CheckItem>Phone number for verification</CheckItem>
                    <CheckItem>Government-issued ID</CheckItem>
                    <CheckItem>Social Security Number (SSN) or EIN</CheckItem>
                  </div>
                  <div className="space-y-2">
                    <CheckItem>Bank account for royalties</CheckItem>
                    <CheckItem>Routing number</CheckItem>
                    <CheckItem>Physical address (not P.O. Box for tax)</CheckItem>
                    <CheckItem>30-60 minutes of time</CheckItem>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Requirements */}
            <Section id="requirements" title="Account Requirements" icon={FileText}>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-3">Step 1: Create an Amazon Account</h4>
                  <p className="text-gray-600 mb-3">
                    If you dont have an Amazon account, create one first at amazon.com. 
                    Use an email you check regularly - Amazon sends important publishing notifications here.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <strong>Pro Tip:</strong> Use a professional email (yourname@domain.com) rather than 
                    a personal one. This looks more professional if readers contact you.
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-3">Step 2: Go to KDP Sign-Up</h4>
                  <div className="flex items-center gap-3 p-4 bg-gray-100 rounded-lg mb-3">
                    <code className="flex-1 text-sm">https://kdp.amazon.com</code>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => copyToClipboard('https://kdp.amazon.com', 'kdp-url')}
                    >
                      {copiedItem === 'kdp-url' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                    <Button 
                      size="sm"
                      onClick={() => window.open('https://kdp.amazon.com', '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-1" /> Open
                    </Button>
                  </div>
                  <p className="text-gray-600">
                    Click Sign in and use your Amazon account credentials. If you dont have an 
                    account, youll be prompted to create one.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-3">Step 3: Complete Your Author Profile</h4>
                  <div className="space-y-2">
                    <CheckItem>
                      <strong>Author/Publisher Name:</strong> This appears on your books. Use your real name 
                      or a pen name. You can change this later.
                    </CheckItem>
                    <CheckItem>
                      <strong>Contact Information:</strong> Address where Amazon can reach you. 
                      This is NOT shown publicly.
                    </CheckItem>
                    <CheckItem>
                      <strong>Payment Information:</strong> Bank account details for royalty deposits.
                    </CheckItem>
                    <CheckItem>
                      <strong>Tax Information:</strong> Required for IRS reporting (see Tax section below).
                    </CheckItem>
                  </div>
                </div>
              </div>
            </Section>

            {/* Tax Information */}
            <Section id="taxInfo" title="Tax Information (W-9/W-8)" icon={DollarSign}>
              <div className="space-y-6">
                <WarningItem>
                  <strong>IMPORTANT:</strong> Amazon will withhold 24-30% of your royalties if you dont 
                  complete tax information correctly. This is money youll never get back!
                </WarningItem>

                <div>
                  <h4 className="font-semibold text-lg mb-3">For U.S. Publishers (W-9)</h4>
                  <p className="text-gray-600 mb-3">
                    As a U.S. person (citizen or resident), youll complete a W-9 form. Youll need:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <CheckItem><strong>Legal Name:</strong> Must match your tax return exactly</CheckItem>
                    <CheckItem><strong>Business Name:</strong> If publishing under an LLC or business</CheckItem>
                    <CheckItem><strong>Tax Classification:</strong> Individual, LLC, Corporation, etc.</CheckItem>
                    <CheckItem><strong>Address:</strong> Your current legal address</CheckItem>
                    <CheckItem><strong>TIN:</strong> SSN (Social Security Number) or EIN (Employer ID Number)</CheckItem>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <h5 className="font-semibold text-green-800 mb-2">💡 SSN vs EIN - Which Should You Use?</h5>
                  <div className="text-green-800 space-y-2">
                    <p><strong>SSN (Social Security Number):</strong> Fine if youre just starting out as an individual.</p>
                    <p><strong>EIN (Employer Identification Number):</strong> Recommended if:</p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li>You plan to publish multiple books</li>
                      <li>You want to separate business and personal identity</li>
                      <li>Youre forming an LLC or corporation</li>
                      <li>You want extra privacy (EIN doesnt reveal SSN)</li>
                    </ul>
                    <p className="mt-2">
                      <strong>Get an EIN for free:</strong> Apply at irs.gov - takes 5 minutes online!
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-3">For Non-U.S. Publishers (W-8BEN)</h4>
                  <p className="text-gray-600 mb-3">
                    Non-U.S. residents complete W-8BEN form. Key requirements:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <CheckItem>Country of citizenship</CheckItem>
                    <CheckItem>Foreign Tax Identification Number (if applicable)</CheckItem>
                    <CheckItem>Tax treaty benefits claim (can reduce withholding)</CheckItem>
                    <CheckItem>Foreign address</CheckItem>
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    Note: Withholding rates vary by country. Some countries have tax treaties with 
                    the U.S. that reduce withholding to 0%.
                  </p>
                </div>
              </div>
            </Section>

            {/* Bank/Payment Information */}
            <Section id="bankInfo" title="Bank & Payment Setup" icon={Building}>
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold text-lg mb-3">Required Banking Information</h4>
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <CheckItem><strong>Bank Name:</strong> Your banks official name</CheckItem>
                    <CheckItem><strong>Account Holder Name:</strong> Must match the name on your account</CheckItem>
                    <CheckItem><strong>Account Number:</strong> Your checking or savings account number</CheckItem>
                    <CheckItem><strong>Routing Number:</strong> 9-digit number (for U.S. banks)</CheckItem>
                  </div>
                </div>

                <WarningItem>
                  <strong>Double-check your numbers!</strong> Amazon will make a small test deposit. 
                  If your banking info is wrong, royalties will bounce and you wont get paid until fixed.
                </WarningItem>

                <div>
                  <h4 className="font-semibold text-lg mb-3">Payment Schedule</h4>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="mb-2">Amazon pays royalties approximately <strong>60 days</strong> after the end of each month.</p>
                    <p className="text-sm text-gray-600">
                      Example: January sales are paid in late March. Minimum payout threshold is $10 for 
                      electronic transfer.
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-3">International Publishers</h4>
                  <p className="text-gray-600 mb-3">
                    You can receive payments in multiple currencies. Amazon supports direct deposit in:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {['USD', 'GBP', 'EUR', 'JPY', 'CAD', 'AUD', 'BRL', 'MXN', 'INR'].map(currency => (
                      <span key={currency} className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium">
                        {currency}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Section>

            {/* Business Structure */}
            <Section id="businessStructure" title="Business Structure Options" icon={Shield}>
              <div className="space-y-6">
                <p className="text-gray-600">
                  You can publish on KDP as an individual or through a business entity. 
                  Heres what to consider:
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-green-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg text-green-800">✓ Individual (Sole Proprietor)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm">
                        <li>✓ Simplest to set up</li>
                        <li>✓ No separate tax filing required</li>
                        <li>✓ Good for testing the waters</li>
                        <li>✗ No liability protection</li>
                        <li>✗ Uses your SSN</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg text-blue-800">✓ LLC (Recommended)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 text-sm">
                        <li>✓ Liability protection</li>
                        <li>✓ Professional appearance</li>
                        <li>✓ Tax flexibility</li>
                        <li>✓ Uses EIN (more privacy)</li>
                        <li>✗ State filing fees ($50-500)</li>
                        <li>✗ Annual reports in some states</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-purple-50 border border-purple-200 p-4 rounded-lg">
                  <h5 className="font-semibold text-purple-800 mb-2">🏆 Best States to Form an LLC</h5>
                  <div className="text-purple-800">
                    <p className="mb-2">Popular choices for online businesses:</p>
                    <ul className="space-y-1">
                      <li><strong>Wyoming:</strong> $100/year, no state income tax, strong privacy</li>
                      <li><strong>Delaware:</strong> $300/year, business-friendly courts</li>
                      <li><strong>Your Home State:</strong> Often simplest if you live/work there</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Section>

            {/* International Publishers */}
            <Section id="international" title="International Publishers Guide" icon={Globe}>
              <div className="space-y-6">
                <p className="text-gray-600">
                  Amazon KDP is available to publishers worldwide. Here are key considerations 
                  for non-U.S. publishers:
                </p>

                <div>
                  <h4 className="font-semibold text-lg mb-3">Tax Treaty Benefits</h4>
                  <p className="text-gray-600 mb-3">
                    Many countries have tax treaties with the U.S. that can reduce or eliminate 
                    the 30% withholding tax on royalties.
                  </p>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="font-semibold text-green-800 mb-2">Countries with 0% withholding:</p>
                    <div className="flex flex-wrap gap-2">
                      {['UK', 'Canada', 'Germany', 'France', 'Australia', 'Netherlands', 'Japan', 'India'].map(country => (
                        <span key={country} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                          {country}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-green-700 mt-2">
                      * Requires valid tax ID and correctly completed W-8BEN form
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-lg mb-3">ITIN for Non-U.S. Residents</h4>
                  <p className="text-gray-600 mb-3">
                    If your country doesnt have a tax treaty with the U.S., consider applying for an 
                    ITIN (Individual Taxpayer Identification Number) to potentially reduce withholding.
                  </p>
                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <p className="text-yellow-800">
                      <strong>Note:</strong> Getting an ITIN can take 8-12 weeks. Plan ahead if you 
                      expect significant royalties.
                    </p>
                  </div>
                </div>
              </div>
            </Section>

            {/* Common Mistakes */}
            <Section id="commonMistakes" title="Common Mistakes to Avoid" icon={AlertTriangle}>
              <div className="space-y-4">
                <WarningItem>
                  <strong>Mismatched Names:</strong> Your KDP account name must match your bank account 
                  and tax forms exactly. John Smith ≠ John D. Smith ≠ J. Smith
                </WarningItem>

                <WarningItem>
                  <strong>Wrong Tax Classification:</strong> Selecting the wrong entity type can cause 
                  tax complications. When in doubt, consult a tax professional.
                </WarningItem>

                <WarningItem>
                  <strong>Skipping Tax Interview:</strong> Not completing the tax interview means 24-30% 
                  of your royalties are withheld indefinitely.
                </WarningItem>

                <WarningItem>
                  <strong>Using P.O. Box for Tax Address:</strong> IRS requires a physical address for 
                  tax purposes. Use your home or business address.
                </WarningItem>

                <WarningItem>
                  <strong>Incorrect Banking Details:</strong> Triple-check your routing and account numbers. 
                  One wrong digit means delayed payments.
                </WarningItem>

                <WarningItem>
                  <strong>Not Verifying Identity:</strong> Amazon may ask for ID verification. Delays in 
                  providing documentation can freeze your account.
                </WarningItem>
              </div>
            </Section>

            {/* CTA Section */}
            <Card className="mt-8 bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Ready to Start Publishing?</h3>
                <p className="text-purple-100 mb-6 max-w-2xl mx-auto">
                  Now that you know how to set up your KDP account, use Kindle Research Pro to 
                  find profitable niches and generate winning book ideas!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => navigate('/kindle-research')}
                    className="bg-white text-purple-600 hover:bg-purple-50"
                    size="lg"
                  >
                    Launch Kindle Research Pro
                  </Button>
                  <Button 
                    onClick={() => window.open('https://kdp.amazon.com', '_blank')}
                    variant="outline"
                    className="border-white text-white hover:bg-white/10"
                    size="lg"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open Amazon KDP
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <p className="text-xs text-gray-500 text-center mt-8 max-w-2xl mx-auto">
              <strong>Disclaimer:</strong> This guide is for informational purposes only and does not 
              constitute legal or tax advice. Tax laws and Amazon policies change frequently. 
              Consult with a qualified tax professional or attorney for advice specific to your situation.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default KDPSetupGuide;
